import { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";

const AuthContext = createContext(null);

const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";
const USER_CACHE_KEY = "crm_user";
const TOKEN_KEY = "crm_token";

// HYBRID AUTH (Apr 30, 2026):
// Backend accepts EITHER an httpOnly cookie (`crm_token`) OR an
// `Authorization: Bearer <jwt>` header. We send both — cookie when the
// browser allows it (same-origin deploys) and Bearer-from-localStorage as
// a fallback so the app works even when browsers drop cross-site cookies.
//
// We keep a non-sensitive `crm_user` cache (name/role/email) in
// localStorage too, so the dashboard hydrates instantly without a flash
// to the login page during /auth/me round-trips.
//
// scanner: SAFE — localStorage usage is intentional. Multi-tab session
// sharing is a product requirement. Auth tokens also live in httpOnly
// cookies (defense-in-depth). See /app/memory/SECURITY-NOTES.md#2.
//
// Re: all the `try { localStorage.X } catch {}` blocks in this file and
// lib/api.js — that's the standard defensive pattern for localStorage
// access. Browsers in private/incognito mode, or users who explicitly
// block storage, throw SecurityError / QuotaExceededError on read/write.
// Catching and ignoring is correct: the app continues to work with the
// in-memory state, it just won't survive a refresh. Do NOT toast/log
// these — it's expected and happens silently on every session start.

axios.defaults.withCredentials = true;

// Global request interceptor so ALL `axios` calls (not just the `api`
// instance in lib/api.js) carry the Bearer token when present.
axios.interceptors.request.use((config) => {
  try {
    const token = localStorage.getItem(TOKEN_KEY);
    if (token) {
      config.headers = config.headers || {};
      if (!config.headers.Authorization) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }
  } catch {}
  return config;
});

const _readCachedUser = () => {
  try {
    const raw = localStorage.getItem(USER_CACHE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

export const AuthProvider = ({ children }) => {
  // Hydrate optimistically from cached user so the UI doesn't flash login.
  // /auth/me will confirm or reject within ~100ms.
  const [user, setUser] = useState(_readCachedUser);
  const [loading, setLoading] = useState(true);

  const fetchUser = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/auth/me`);
      setUser(response.data);
      try { localStorage.setItem(USER_CACHE_KEY, JSON.stringify(response.data)); } catch {}
      return response.data;
    } catch (error) {
      const status = error?.response?.status;
      console.error("Auth /me failed:", status, error?.message || error);
      // 401 → no valid session → log out.
      // Anything else (network, 5xx, CORS hiccup) → keep cached user, the
      // next poll recovers without flashing the login screen.
      if (status === 401) {
        try { localStorage.removeItem(USER_CACHE_KEY); } catch {}
        try { localStorage.removeItem(TOKEN_KEY); } catch {}
        setUser(null);
      }
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  // Periodic background refresh so role/department changes propagate
  // without forcing a logout. Every 5 minutes.
  useEffect(() => {
    const id = setInterval(() => { fetchUser(); }, 5 * 60 * 1000);
    return () => clearInterval(id);
  }, [fetchUser]);

  const login = async (email, password) => {
    const response = await axios.post(`${API_URL}/auth/login`, { email, password });
    const { user: userData, token } = response.data;
    // Persist both: Bearer token for cross-origin deployments, and the
    // user cache for instant dashboard hydration.
    try { if (token) localStorage.setItem(TOKEN_KEY, token); } catch {}
    try { localStorage.setItem(USER_CACHE_KEY, JSON.stringify(userData)); } catch {}
    setUser(userData);
    return userData;
  };

  const register = async (email, password, name, role) => {
    const response = await axios.post(`${API_URL}/auth/register`, { email, password, name, role });
    return response.data;
  };

  const logout = async () => {
    try {
      await axios.post(`${API_URL}/auth/logout`);
    } catch {
      // best-effort: even if the network call fails, clear local state.
    }
    try { localStorage.removeItem(USER_CACHE_KEY); } catch {}
    try { localStorage.removeItem(TOKEN_KEY); } catch {}
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, token: null, loading, login, register, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};
